/**
 * Marketing Terminal — Media Relations
 * Master spec §2.9. Outlets + pitches + appearances.
 */

import React, { useState } from 'react';
import { Calendar, Mic, Plus, Send, Tv, Newspaper } from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '../../../utils/adminApi';

interface Outlet {
  id: string; type: string; name: string; description: string;
  audience_size: number | null; topic_tags: string[];
  fit_score: number | null; status: string; external_url: string | null;
}
interface Pitch {
  id: string; outlet_id: string; outlet_name?: string; outlet_type?: string;
  contact_name?: string; angle: string; pitch_subject: string | null;
  status: string; sent_at: string | null; booked_for: string | null;
  followup_count: number; updated_at: string;
}
interface Appearance {
  id: string; outlet_name?: string; outlet_type?: string; type: string;
  title: string; aired_at: string | null; media_url: string | null;
  traffic_session_count: number | null; trial_signups_attributed: number | null;
}

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 30_000 } } });

export function MediaRelationsPage() {
  return (
    <QueryClientProvider client={queryClient}>
      <Inner />
    </QueryClientProvider>
  );
}

function Inner() {
  const [tab, setTab] = useState<'outlets' | 'pitches' | 'appearances'>('pitches');

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "'League Spartan', sans-serif", color: 'var(--cedar-ink)' }}>
          Media Relations
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Podcast / radio / newsletter / journalist outreach. Discovery → pitch → booked → aired.
        </p>
      </div>

      <div className="flex gap-2 border-b border-gray-200">
        {[
          { key: 'pitches', label: 'Pitches', icon: Send },
          { key: 'outlets', label: 'Outlets', icon: Mic },
          { key: 'appearances', label: 'Appearances', icon: Tv },
        ].map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key as 'outlets' | 'pitches' | 'appearances')}
              className="flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium"
              style={active ? { borderColor: 'var(--cedar-primary)', color: 'var(--cedar-primary)' } : { borderColor: 'transparent', color: 'var(--text-secondary)' }}
            >
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'outlets' && <OutletsTab />}
      {tab === 'pitches' && <PitchesTab />}
      {tab === 'appearances' && <AppearancesTab />}
    </div>
  );
}

function OutletsTab() {
  const [type, setType] = useState('');
  const { data } = useQuery<{ data: Outlet[] }>({
    queryKey: ['marketing-outlets', type],
    queryFn: () => {
      const qs = type ? `?type=${type}` : '';
      return adminFetch(`/api/admin/marketing/media/outlets${qs}`);
    },
  });

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {['', 'podcast', 'radio', 'newsletter', 'youtube', 'publication'].map(t => (
          <button
            key={t || 'all'}
            onClick={() => setType(t)}
            className="rounded px-2 py-1 text-xs font-medium"
            style={type === t ? { backgroundColor: 'var(--cedar-primary)', color: '#fff' } : { backgroundColor: 'var(--cedar-limestone)', color: 'var(--text-secondary)' }}
          >
            {t || 'all'}
          </button>
        ))}
      </div>

      {data?.data?.length === 0 ? (
        <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-400" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1)' }}>
          <Mic className="mx-auto mb-2 h-8 w-8 text-gray-300" />
          No outlets discovered yet. Listen Notes integration populates this when configured.
        </div>
      ) : (
        <div className="space-y-2">
          {data?.data?.map(o => (
            <div key={o.id} className="rounded-lg bg-white p-3" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.06)' }}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase">{o.type}</span>
                    <span className="text-sm font-bold text-gray-900">{o.name}</span>
                  </div>
                  {o.description && <div className="mt-1 line-clamp-2 text-xs text-gray-600">{o.description}</div>}
                  <div className="mt-1 text-[10px] text-gray-500">
                    {o.audience_size && `${o.audience_size.toLocaleString()} audience`}
                    {o.fit_score !== null && ` · Fit: ${o.fit_score}/100`}
                    {o.status && ` · ${o.status}`}
                  </div>
                </div>
                {o.fit_score !== null && (
                  <div
                    className="rounded px-2 py-1 text-xs font-bold"
                    style={{
                      backgroundColor: o.fit_score >= 70 ? 'var(--cedar-subtle)' : o.fit_score >= 40 ? 'var(--status-warning-bg)' : 'var(--cedar-limestone)',
                      color: o.fit_score >= 70 ? 'var(--cedar-primary)' : o.fit_score >= 40 ? 'var(--status-warning-text)' : 'var(--text-secondary)',
                    }}
                  >
                    {Math.round(o.fit_score)}%
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PitchesTab() {
  const queryClient = useQueryClient();
  const { data } = useQuery<{ data: Pitch[] }>({
    queryKey: ['marketing-pitches'],
    queryFn: () => adminFetch('/api/admin/marketing/media/pitches'),
  });

  const send = useMutation({
    mutationFn: (id: string) =>
      adminFetch(`/api/admin/marketing/media/pitches/${id}/send`, {
        method: 'POST',
        body: JSON.stringify({ sent_via: 'manual_email' }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing-pitches'] }),
  });

  const grouped: Record<string, Pitch[]> = {};
  data?.data?.forEach(p => {
    grouped[p.status] = grouped[p.status] ?? [];
    grouped[p.status].push(p);
  });

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-5">
      {['drafted', 'sent', 'responded', 'booked', 'declined'].map(status => (
        <div key={status} className="rounded-lg bg-white p-3" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.06)' }}>
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600">{status}</h4>
            <span className="text-xs font-bold text-gray-800">{grouped[status]?.length ?? 0}</span>
          </div>
          <div className="space-y-2">
            {grouped[status]?.map(p => (
              <div key={p.id} className="rounded border border-gray-100 bg-gray-50 p-2 text-xs">
                <div className="font-medium text-gray-900">{p.outlet_name}</div>
                <div className="line-clamp-2 text-[11px] text-gray-600">{p.angle}</div>
                {status === 'drafted' && (
                  <button
                    onClick={() => send.mutate(p.id)}
                    className="mt-1 rounded px-2 py-0.5 text-[10px] font-medium text-white"
                    style={{ backgroundColor: 'var(--cedar-primary)' }}
                  >
                    Mark sent
                  </button>
                )}
                {p.booked_for && (
                  <div className="mt-1 text-[10px]" style={{ color: 'var(--cedar-primary)' }}>
                    <Calendar className="mr-0.5 inline h-2.5 w-2.5" />
                    {new Date(p.booked_for).toLocaleDateString()}
                  </div>
                )}
              </div>
            ))}
            {!grouped[status]?.length && <div className="text-[10px] italic text-gray-300">empty</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function AppearancesTab() {
  const { data } = useQuery<{ data: Appearance[] }>({
    queryKey: ['marketing-appearances'],
    queryFn: () => adminFetch('/api/admin/marketing/media/appearances'),
  });

  if (!data?.data?.length) {
    return <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-400" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1)' }}>
      <Tv className="mx-auto mb-2 h-8 w-8 text-gray-300" />
      No published appearances yet.
    </div>;
  }

  return (
    <div className="space-y-2">
      {data.data.map(a => (
        <div key={a.id} className="rounded-lg bg-white p-3" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.06)' }}>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
                {a.outlet_name} {a.title && `· ${a.title}`}
              </div>
              <div className="text-[10px] text-gray-500">
                {a.outlet_type} · {a.aired_at ? `aired ${new Date(a.aired_at).toLocaleDateString()}` : 'unscheduled'}
              </div>
            </div>
            <div className="text-right text-[10px] text-gray-500">
              {a.traffic_session_count ? `${a.traffic_session_count.toLocaleString()} sessions` : '—'}
              {a.trial_signups_attributed && (
                <div className="font-bold" style={{ color: 'var(--cedar-primary)' }}>
                  {a.trial_signups_attributed} trials
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default MediaRelationsPage;
